import { format, isDate, differenceInCalendarDays, differenceInSeconds, intlFormatDistance } from 'date-fns';
import { uk, enGB, es, pt } from 'date-fns/locale';
import { LANGUAGE } from '@constants/language';
import { LOCALE } from '@constants/locale';
import { GroupByFilter } from '@constants/analytics';

const SUNDAY_FIRST_LOCALES = [LOCALE.UNITED_STATES, LOCALE.BRAZIL];

export const getWeekStartsOn = accountLocale => (SUNDAY_FIRST_LOCALES.includes(accountLocale) ? 0 : 1);

export const I18N_DATE_FORMAT = {
    [LANGUAGE.ENGLISH]: enGB,
    [LANGUAGE.UKRAINIAN]: uk,
    [LANGUAGE.PORTUGUESE]: pt,
    [LANGUAGE.PORTUGUESE_BRAZIL]: pt,
    [LANGUAGE.SPANISH]: es,
    [LANGUAGE.SPANISH_MEXICO]: es,
};

export const INTL_DATE_FORMAT = {
    [LANGUAGE.ENGLISH]: 'en',
    [LANGUAGE.UKRAINIAN]: 'uk',
    [LANGUAGE.PORTUGUESE]: 'pt',
    [LANGUAGE.PORTUGUESE_BRAZIL]: 'pt',
    [LANGUAGE.SPANISH]: 'es',
    [LANGUAGE.SPANISH_MEXICO]: 'es',
};

export const EMPTY_DATE_PLACEHOLDER = '';

export const isUnsetDate = date => {
    if (!date) return true;
    const dateObj = new Date(date);
    return isNaN(dateObj.getTime()) || dateObj.getFullYear() <= 1;
};

export const isMaxDate = date => {
    if (!date) return false;
    const dateObj = new Date(date);
    return !isNaN(dateObj.getTime()) && dateObj.getFullYear() >= 9999;
};

export const formatDateOrEmpty = (date, formatStr, language) => {
    if (isUnsetDate(date)) return EMPTY_DATE_PLACEHOLDER;
    return format(new Date(date), formatStr, { locale: I18N_DATE_FORMAT[language] });
};

export const toTimestamp = date => (isUnsetDate(date) ? null : new Date(date).getTime());

export const getHumanizedDate = (date, language) => {
    if (isUnsetDate(date)) return EMPTY_DATE_PLACEHOLDER;

    const dateObj = new Date(date);
    const diffInDays = Math.abs(differenceInCalendarDays(dateObj, new Date()));

    if (diffInDays < 8) {
        return intlFormatDistance(dateObj, new Date(), {
            addSuffix: true,
            locale: INTL_DATE_FORMAT[language],
        });
    }

    return format(dateObj, 'dd MMM', { locale: I18N_DATE_FORMAT[language] });
};

export const getExpiredAtInfo = (date, language) => ({
    expiredAt: formatDateOrEmpty(date, 'd MMMM yyyy', language),
});

export function getDifferenceInDates(date, planGracePeriod = 0) {
    let billingDiffInDays = 0;
    let billingDiffInSeconds = 0;
    if (isDate(date)) {
        //expiration account days
        billingDiffInDays = differenceInCalendarDays(date, new Date()) - planGracePeriod;
        //blocked account days
        billingDiffInSeconds = differenceInSeconds(date, new Date());
    }

    return { billingDiffInDays, billingDiffInSeconds };
}

export function subtractYears(date, years) {
    date.setFullYear(date.getFullYear() - years);
    return date;
}

export const MINUTES_IN_DAY = 24 * 60;
const MS_IN_MINUTE = 60 * 1000;
const WEEK_DAYS = [1, 2, 3, 4, 5, 6, 0];

export const toMinutes = time => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
};

export const toTime = minutes =>
    `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

const isKnownTimeZone = zone => {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: zone });
        return true;
    } catch (_) {
        return false;
    }
};

const wallClockOf = (zone, instant) => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        hourCycle: 'h23',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    }).formatToParts(instant);
    const read = type => Number(parts.find(part => part.type === type).value);
    return Date.UTC(read('year'), read('month') - 1, read('day'), read('hour') % 24, read('minute'));
};

const mergeIntervals = intervals =>
    [...intervals]
        .sort((a, b) => a.from - b.from)
        .reduce((merged, interval) => {
            const last = merged[merged.length - 1];
            if (last && interval.from <= last.to) {
                last.to = Math.max(last.to, interval.to);
            } else {
                merged.push({ ...interval });
            }
            return merged;
        }, []);

const splitAcrossDays = (day, from, to) => {
    const pieces = [];
    for (let shift = Math.floor(from / MINUTES_IN_DAY); shift <= Math.floor((to - 1) / MINUTES_IN_DAY); shift++) {
        pieces.push({
            day: (((day + shift) % 7) + 7) % 7,
            from: Math.max(from - shift * MINUTES_IN_DAY, 0),
            to: Math.min(to - shift * MINUTES_IN_DAY, MINUTES_IN_DAY),
        });
    }
    return pieces;
};

const offsetOf = (zone, instant) => Math.round((wallClockOf(zone, instant) - instant.getTime()) / MS_IN_MINUTE);

const zoneFromOffset = (offsetMinutes, candidates, instant) => {
    if (offsetMinutes % 60 === 0) {
        const hours = offsetMinutes / 60;
        if (hours === 0) return 'UTC';
        return `Etc/GMT${hours > 0 ? '-' : '+'}${Math.abs(hours)}`;
    }
    return candidates.find(zone => isKnownTimeZone(zone) && offsetOf(zone, instant) === offsetMinutes) || 'UTC';
};

export const resolveTimeZone = (zoneName, offsetMinutes, candidates = [], instant = new Date()) =>
    zoneName && isKnownTimeZone(zoneName) ? zoneName : zoneFromOffset(offsetMinutes, candidates, instant);

export const getBrowserTimeZone = () =>
    resolveTimeZone(
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        -new Date().getTimezoneOffset(),
        typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [],
    );

export const formatTimeZoneLabel = zone => {
    const fixedOffset = /^Etc\/GMT([+-])(\d{1,2})$/.exec(zone || '');
    return fixedOffset ? `UTC${fixedOffset[1] === '-' ? '+' : '-'}${fixedOffset[2]}` : zone;
};

export const convertHoursBetweenZones = (days, fromZone, toZone, referenceDate = new Date()) => {
    const source = isKnownTimeZone(fromZone) ? fromZone : 'UTC';
    const target = isKnownTimeZone(toZone) ? toZone : 'UTC';
    if (!days?.length || source === target) return days || [];

    const delta = Math.round((wallClockOf(target, referenceDate) - wallClockOf(source, referenceDate)) / MS_IN_MINUTE);
    const byDay = new Map();
    days.forEach(day => {
        (day.hours?.length ? day.hours : [{ from: '00:00', to: '24:00' }]).forEach(({ from, to }) => {
            const start = toMinutes(from);
            const end = toMinutes(to) <= start ? toMinutes(to) + MINUTES_IN_DAY : toMinutes(to);
            splitAcrossDays(day.day, start + delta, end + delta).forEach(piece => {
                byDay.set(piece.day, [...(byDay.get(piece.day) || []), piece]);
            });
        });
    });

    return WEEK_DAYS.filter(day => byDay.has(day)).map(day => ({
        ...(days.find(item => item.day === day) || {}),
        day,
        hours: mergeIntervals(byDay.get(day)).map(({ from, to }) => ({ from: toTime(from), to: toTime(to) })),
    }));
};

export const generateTimeOptions = () => {
    const options = [];

    for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
            const formattedHour = String(hour).padStart(2, '0');
            const formattedMinute = String(minute).padStart(2, '0');
            options.push(`${formattedHour}:${formattedMinute}`);
        }
    }

    return options.map(i => ({ value: i, label: i }));
};

export const formatTimeframeChipValue = (start, end, language) => {
    const locale = { locale: I18N_DATE_FORMAT[language] };
    const startFormatted = format(new Date(start), 'd MMM yyyy', locale);
    if (!end) return startFormatted;
    return `${startFormatted} - ${format(new Date(end), 'd MMM yyyy', locale)}`;
};

export const getChartDateFormat = grouping => {
    if (grouping === GroupByFilter.Months) return 'MMM yy';
    if (grouping === GroupByFilter.Years) return 'yyyy';
    return 'dd MMM';
};

export const getMonthsList = locale => {
    const months = [];
    for (let i = 0; i < 12; i++) {
        const monthDate = new Date(Date.UTC(2024, i, 15)); // INFO: 15 is a random day in the mid of the month
        months.push(format(monthDate, 'LLLL', { locale }));
    }
    return months;
};
